import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { syncUserPermissions } from '../utils/permissionSync';
import { useAuth } from '../hooks/useAuth';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: {
    features: string[];
    contests: string[];
    contest_permissions: Record<string, string[]>;
  };
  is_active: boolean;
}

export const useUser = () => {
  const { user: authUser } = useAuth();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      if (!authUser?.id) {
        setUser(null);
        setIsLoading(false);
        return;
      }

      try {
        // Récupérer les données de l'utilisateur
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('*')
          .eq('id', authUser.id)
          .single();

        if (userError) throw userError;

        // Synchroniser les permissions
        const permissions = await syncUserPermissions(authUser.id);

        if (userData) {
          setUser({
            ...userData,
            permissions: permissions || {
              features: [],
              contests: [],
              contest_permissions: {}
            }
          });
        }
      } catch (error) {
        console.error('Erreur lors de la récupération de l\'utilisateur:', error);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [authUser]);

  return { user, isLoading };
};

export default useUser;
